import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { RGBColor } from 'react-color';
import { useDispatch, useSelector } from 'react-redux';

import {
  ACCENT_COLOR,
  APP_BAR_BACKGROUND_COLOR,
  BODY_TEXT,
  DEFAULT_WHITE_COLOR,
  HEADER_MEDIA,
  HEADER_TEXT,
  HERO_SECTION_BACKGROUND_COLOR,
  HERO_SECTION_BACKGROUND_TYPE,
  HERO_SECTION_IMAGE_FIT,
  HERO_SECTION_IMAGE_URL,
  LOGO_IMAGE,
  LOGO_IMAGE_SIZE,
  PAGE_KEY_MAP,
  SIDE_PANEL_EDITOR_DEFAULT_HEIGHT,
} from '../../../../../constants/templates';
import { FormLabel, RichEditor } from '../../../index';
import {
  AppLogoUploader,
  ColorPickerWithReset,
  HeaderMediaUploader,
  HeroBackgroundControl,
  LogoSizeControl,
} from '../../../_components_/TemplateUploadControls';

import { noop } from 'lodash';
import { updateClaimLinkBuilderField } from '../../../../../store/actions/templates';
import { selectCurrentOrganizationLogoUrl } from '../../../../../store/selectors/organizations';
import { selectDefaultExperienceBlueprint } from '../../../../../store/selectors/shell';
import { selectClaimLinkBuilderDraft, selectTemplateValidationErrors } from '../../../../../store/selectors/templates';
import { ImageFitEnum } from '../../../../../types/shell';
import {
  ClaimLinkEditorPageEnum,
  IClaimLinkPageOverrides,
  UploadControlModeEnum,
} from '../../../../../types/templates';
import { getCountLabel } from '../../../../../utils/helpers';
import {
  getPageFieldError,
  getRelatedOverrideFields,
  initializeClaimLinkBuilderFieldTracking,
  resolveHeroBackgroundType,
} from '../../../../../utils/templates';
import { thinScrollbarStyles } from '../../../../../utils/ui';

const paperStyles = {
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowX: 'hidden',
  // Keep the scroll position pinned while an editor is resized; without this the browser
  // re-anchors to the drag point at the bottom and scrolls the field's label/toolbar out of view.
  overflowAnchor: 'none',
  borderRadius: 0.5,
  ...thinScrollbarStyles,
};

interface IProps {
  shouldShowError: (fieldConstant: string, pageId?: ClaimLinkEditorPageEnum) => boolean;
  handleFieldChange: (fieldConstant: string, newValue: unknown, pageId?: ClaimLinkEditorPageEnum) => boolean;
  initializeFieldValue: (fieldKey: string, value: unknown) => void;
}

export const SidePanel = ({ shouldShowError, handleFieldChange, initializeFieldValue }: IProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const templateDraft = useSelector(selectClaimLinkBuilderDraft);
  const defaultBlueprint = useSelector(selectDefaultExperienceBlueprint);
  const organizationLogoUrl = useSelector(selectCurrentOrganizationLogoUrl);
  const validationErrors = useSelector(selectTemplateValidationErrors);

  const {
    activePageId,
    pages,
    globalLogoImageUrl,
    globalLogoImageSize,
    globalAccentColor,
    globalAppBarBackgroundColor,
    globalHeaderMediaUrl,
    globalHeroSectionBackgroundColor,
    globalHeroSectionImageUrl,
    globalHeroSectionImageFit,
    globalHeroSectionBackgroundType,
  } = templateDraft;

  const currentPage = pages[activePageId];
  const currentOverrides = currentPage.overrides;

  const defaultLogoImageUrl = useMemo(
    () => globalLogoImageUrl || organizationLogoUrl || '',
    [globalLogoImageUrl, organizationLogoUrl],
  );

  const defaultHeaderMediaUrl = useMemo(() => defaultBlueprint?.header?.media?.image_url || '', [defaultBlueprint]);

  const defaultHeroSectionImageUrl = useMemo(
    () => defaultBlueprint?.header?.hero_section?.image_url || '',
    [defaultBlueprint],
  );

  const defaultHeroSectionImageFit = useMemo(
    () => defaultBlueprint?.header?.hero_section?.image_fit || ImageFitEnum.Cover,
    [defaultBlueprint],
  );

  const getDefaultColor = useCallback(
    (pick: (bp: typeof defaultBlueprint) => unknown): RGBColor => {
      const picked = pick(defaultBlueprint);
      return ((picked as RGBColor) || DEFAULT_WHITE_COLOR) as RGBColor;
    },
    [defaultBlueprint],
  );

  const defaultButtonColor = useMemo(() => getDefaultColor((bp) => bp?.buttons?.action?.color), [getDefaultColor]);

  const defaultAppBarBackgroundColor = useMemo(
    () => getDefaultColor((bp) => bp?.header?.app_bar?.background_color),
    [getDefaultColor],
  );

  const [previewLogoImageSize, setPreviewLogoImageSize] = React.useState<number | null>(null);

  const effectiveLogoImageSize = useMemo(
    () => previewLogoImageSize ?? currentOverrides.logoImageSize ?? globalLogoImageSize,
    [previewLogoImageSize, currentOverrides.logoImageSize, globalLogoImageSize],
  );

  const defaultHeroSectionBackgroundColor = useMemo(
    () => getDefaultColor((bp) => bp?.header?.hero_section?.background_color),
    [getDefaultColor],
  );

  useEffect(() => {
    initializeClaimLinkBuilderFieldTracking(pages, PAGE_KEY_MAP, initializeFieldValue);
  }, [initializeFieldValue, pages]);

  const headerTextError = useMemo(
    () => getPageFieldError(HEADER_TEXT, activePageId, validationErrors, shouldShowError),
    [activePageId, validationErrors, shouldShowError],
  );

  // Active hero background selection for the current page: page override → global → derived
  // from whether an image is effectively set on this page.
  const heroBackgroundType = useMemo(() => {
    const displayImageUrl =
      currentOverrides.heroSectionImageUrl !== undefined
        ? currentOverrides.heroSectionImageUrl
        : globalHeroSectionImageUrl;
    return resolveHeroBackgroundType(
      currentOverrides.heroSectionBackgroundType ?? globalHeroSectionBackgroundType,
      displayImageUrl,
    );
  }, [
    currentOverrides.heroSectionBackgroundType,
    currentOverrides.heroSectionImageUrl,
    globalHeroSectionBackgroundType,
    globalHeroSectionImageUrl,
  ]);

  const heroSectionErrorCount = headerTextError ? 1 : 0;

  const handlePageContentChange = useCallback(
    (field: typeof HEADER_TEXT | typeof BODY_TEXT) => (value: string) => {
      handleFieldChange(field, value, activePageId);

      dispatch(
        updateClaimLinkBuilderField({
          pages: {
            [activePageId]: {
              ...pages[activePageId],
              [field]: value,
            },
          },
        }),
      );
    },
    [activePageId, pages, handleFieldChange, dispatch],
  );

  // Handler for setting override (removes undefined)
  const handleOverrideChange = useCallback(
    (field: keyof IClaimLinkPageOverrides) => (value: IClaimLinkPageOverrides[keyof IClaimLinkPageOverrides]) => {
      dispatch(
        updateClaimLinkBuilderField({
          pages: {
            [activePageId]: {
              ...pages[activePageId],
              overrides: {
                ...pages[activePageId].overrides,
                [field]: value,
              },
            },
          },
        }),
      );
    },
    [activePageId, pages, dispatch],
  );

  // Handler to clear override (set to undefined)
  const handleClearOverride = useCallback(
    (field: keyof IClaimLinkPageOverrides) => () => {
      const newOverrides: IClaimLinkPageOverrides = {
        ...pages[activePageId].overrides,
      };

      const fieldsToClear = getRelatedOverrideFields(field);
      fieldsToClear.forEach((fieldToClear) => {
        newOverrides[fieldToClear as keyof IClaimLinkPageOverrides] = undefined;
      });

      dispatch(
        updateClaimLinkBuilderField({
          pages: {
            [activePageId]: {
              ...pages[activePageId],
              overrides: newOverrides,
            },
          },
        }),
      );
    },
    [activePageId, pages, dispatch],
  );

  const handleLogoUpload = useCallback(
    (url: string) => {
      handleOverrideChange(LOGO_IMAGE)(url);
    },
    [handleOverrideChange],
  );

  // Atomically update multiple override fields in a single dispatch to avoid stale-state overwrites.
  const updateOverrides = useCallback(
    (overrideUpdates: Partial<IClaimLinkPageOverrides>) => {
      dispatch(
        updateClaimLinkBuilderField({
          pages: {
            [activePageId]: {
              ...pages[activePageId],
              overrides: {
                ...pages[activePageId].overrides,
                ...overrideUpdates,
              },
            },
          },
        }),
      );
    },
    [activePageId, pages, dispatch],
  );

  // Remove hero background image on the current page.
  // Sets override to empty string so only this tab loses the image; the fit override
  // (which carries the height mode) belongs to the removed image, so it is cleared too.
  const handleHeroImageRemove = useCallback(() => {
    updateOverrides({ heroSectionImageUrl: '', heroSectionImageFit: undefined });
  }, [updateOverrides]);

  // Clear the image URL and framing overrides to fall back to the defaults modal value.
  const handleResetHeroImageToGlobal = useCallback(() => {
    updateOverrides({ heroSectionImageUrl: undefined, heroSectionImageFit: undefined });
  }, [updateOverrides]);

  const accordionStyles = useMemo(
    () => ({
      my: '0 !important',
      '&::before': { display: 'none' },
      '& .MuiAccordionSummary-root': {
        minHeight: '0 !important',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
      '& .MuiAccordionSummary-content': {
        margin: `${theme.spacing(2)} ${theme.spacing(0)} ${theme.spacing(1)} !important`,
        '&.Mui-expanded': {
          margin: `${theme.spacing(2)} ${theme.spacing(0)} ${theme.spacing(1)} !important`,
        },
      },
      '&.Mui-expanded': {
        margin: '0 !important',
      },
      '& .MuiAccordionDetails-root': {
        paddingTop: '0 !important',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
      },
    }),
    [theme],
  );

  return (
    <Paper sx={paperStyles}>
      <Accordion defaultExpanded={true} elevation={0} sx={accordionStyles}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-action="content_accordion">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              Hero
            </Typography>
            {heroSectionErrorCount > 0 && (
              <Tooltip title={`${getCountLabel(heroSectionErrorCount, 'issue', 'issues')} in this section`} arrow>
                <ErrorOutlineIcon color="error" fontSize="small" />
              </Tooltip>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormLabel label="Header text">
            <RichEditor
              value={currentPage.headerText}
              height={SIDE_PANEL_EDITOR_DEFAULT_HEIGHT}
              onInputChange={handlePageContentChange(HEADER_TEXT)}
              hasColorPicker={true}
              hasTagPicker={true}
              hasAdaptiveBackground={true}
              dataAction="header_text"
              error={!!headerTextError}
              helperText={headerTextError}
            />
          </FormLabel>
          <FormLabel label="Body text">
            <RichEditor
              value={currentPage.bodyText}
              height={SIDE_PANEL_EDITOR_DEFAULT_HEIGHT}
              onInputChange={handlePageContentChange(BODY_TEXT)}
              hasColorPicker={true}
              hasTagPicker={true}
              hasAdaptiveBackground={true}
              dataAction="body_text"
            />
          </FormLabel>
          <HeaderMediaUploader
            mode={UploadControlModeEnum.PageOverride}
            globalValue={globalHeaderMediaUrl}
            overrideValue={currentOverrides.headerMediaUrl}
            defaultValue={defaultHeaderMediaUrl}
            onUpload={(url) => handleOverrideChange(HEADER_MEDIA)(url)}
            onRemove={() => handleOverrideChange(HEADER_MEDIA)('')}
            onReset={noop}
            onClearOverride={() => handleClearOverride(HEADER_MEDIA)()}
            dataAction="header_media"
          />
          <HeroBackgroundControl
            mode={UploadControlModeEnum.PageOverride}
            backgroundType={heroBackgroundType}
            onBackgroundTypeChange={(type) => handleOverrideChange(HERO_SECTION_BACKGROUND_TYPE)(type)}
            globalColor={globalHeroSectionBackgroundColor}
            overrideColor={currentOverrides.heroSectionBackgroundColor}
            defaultColor={defaultHeroSectionBackgroundColor}
            globalImageUrl={globalHeroSectionImageUrl}
            overrideImageUrl={currentOverrides.heroSectionImageUrl}
            defaultImageUrl={defaultHeroSectionImageUrl}
            defaultImageFit={defaultHeroSectionImageFit}
            globalImageFit={globalHeroSectionImageFit}
            overrideImageFit={currentOverrides.heroSectionImageFit}
            onColorChange={(color) => handleOverrideChange(HERO_SECTION_BACKGROUND_COLOR)(color)}
            onColorReset={noop}
            onImageUpload={(url) => handleOverrideChange(HERO_SECTION_IMAGE_URL)(url)}
            onImageRemove={handleHeroImageRemove}
            onImageFitChange={(fit) => handleOverrideChange(HERO_SECTION_IMAGE_FIT)(fit)}
            onClearColorOverride={() => handleClearOverride(HERO_SECTION_BACKGROUND_COLOR)()}
            onClearImageOverride={() => handleClearOverride(HERO_SECTION_IMAGE_URL)()}
            onClearFitOverride={() => handleClearOverride(HERO_SECTION_IMAGE_FIT)()}
            onResetImageToGlobal={handleResetHeroImageToGlobal}
            dataAction="background"
          />
        </AccordionDetails>
      </Accordion>
      <Divider />
      <Accordion defaultExpanded={true} elevation={0} sx={accordionStyles}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-action="app_bar_accordion">
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            App bar
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormLabel label="Logo">
            <AppLogoUploader
              mode={UploadControlModeEnum.PageOverride}
              globalValue={globalLogoImageUrl}
              overrideValue={currentOverrides.logoImageUrl}
              defaultValue={defaultLogoImageUrl}
              onUpload={handleLogoUpload}
              onRemove={() => handleClearOverride(LOGO_IMAGE)()}
              onClearOverride={() => handleClearOverride(LOGO_IMAGE)()}
              logoSizePercent={effectiveLogoImageSize}
              dataAction="app_bar_logo"
            />
          </FormLabel>
          <LogoSizeControl
            mode={UploadControlModeEnum.PageOverride}
            globalValue={globalLogoImageSize}
            overrideValue={currentOverrides.logoImageSize}
            onChange={(percent) => handleOverrideChange(LOGO_IMAGE_SIZE)(percent)}
            onReset={noop}
            onClearOverride={() => handleClearOverride(LOGO_IMAGE_SIZE)()}
            onPreview={setPreviewLogoImageSize}
            dataAction="app_bar_logo_size"
          />
          <FormLabel label="Color">
            <ColorPickerWithReset
              mode={UploadControlModeEnum.PageOverride}
              globalValue={globalAppBarBackgroundColor}
              overrideValue={currentOverrides.appBarBackgroundColor}
              defaultValue={defaultAppBarBackgroundColor}
              onChange={(color) => handleOverrideChange(APP_BAR_BACKGROUND_COLOR)(color)}
              onReset={noop}
              onClearOverride={() => handleClearOverride(APP_BAR_BACKGROUND_COLOR)()}
              dataAction="app_bar_color"
            />
          </FormLabel>
        </AccordionDetails>
      </Accordion>
      <Divider />
      <Accordion defaultExpanded={true} elevation={0} sx={accordionStyles}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-action="theme_accordion">
          <Typography variant="body1" sx={{ lineHeight: 1.7 }} color="text.primary">
            Theme
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormLabel label="Accent color">
            <ColorPickerWithReset
              mode={UploadControlModeEnum.PageOverride}
              globalValue={globalAccentColor}
              overrideValue={currentOverrides.buttonColor}
              defaultValue={defaultButtonColor}
              onChange={(color) => handleOverrideChange(ACCENT_COLOR)(color)}
              onReset={noop}
              onClearOverride={() => handleClearOverride(ACCENT_COLOR)()}
              dataAction="accent_color"
            />
          </FormLabel>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};
